import {
    applyCustomSupersedingRules,
    applyDisplayHiddenMetadata,
} from './superseding'

const age = (x) => Math.round(x / 365) || 1
const REQUEST_TIMEOUT_ERROR_MESSAGE = 'Request timed out'
const MRX_URL = '/api/v3/v4/mrx'

export default {
    async reset({ commit }) {
        commit('reset')
    },

    resume({ commit }, payload) {
        commit('resume', payload)
    },

    async conditions({ getters, rootGetters }) {
        let results = []

        if (!getters.useDeviceMRX && !rootGetters['v4/session/authenticated']) {
            return results
        }

        try {
            if (getters.useDeviceMRX) {
                await getters.loaded
                results = window.mrx.logic.conditions()
            }
            else if (rootGetters['v4/session/authenticated']) {
                const mrxConditionsUrl = `${MRX_URL}/conditions`
                const requestHeaders = {    
                    'Content-Type': 'application/json',
                }

                const response = await fetch(mrxConditionsUrl, {
                    method: 'GET',
                    headers: requestHeaders,
                })

                if (response.status === 401) {
                    return results
                }

                if (!response.ok) {
                    throw new Error(`Request to ${mrxConditionsUrl} returned not-OK status: ${response.status} - ${response.statusText}`)
                }

                results = await response.json()
            }
        }
        catch (e) {
            this._vm.$sentry.withScope(scope => {
                scope.setTag("location", "mrx-condition-fetch")
                this._vm.$sentry.captureException(e)
            });
        }

        return results
    },

    async _assess({ rootGetters, dispatch, getters }, payload) {
        let results = []
        await dispatch('reset')
        let debug = rootGetters['v4/mode/get']('logScores')
        let showAll = rootGetters['v4/mode/get']('showAllAssessments')
        try {
            if (getters.useDeviceMRX) {
                await getters.loaded
                let assessed = await window.mrx.logic.assess(payload, {
                    debug: debug,
                    all: showAll
                })

                if (debug) {
                    console.log(JSON.parse(JSON.stringify(assessed)))
                }

                let cared = await window.mrx.logic.care(assessed, {
                    debug: debug,
                    all: showAll
                })

                results = await window.mrx.logic.treat(cared)

                results.push({
                    type: 'status',
                    name: 'dispatched',
                    value: 'device'
                })
            }
            else {
                const assessUrl = `${MRX_URL}/assess?debug=${debug}&all=${showAll}`
                const requestHeaders = {
                    'Content-Type': 'application/json',
                }
                const requestBody = JSON.stringify([payload])

                const response = await fetch(assessUrl, {
                    method: 'POST',
                    headers: requestHeaders,
                    body: requestBody
                })

                if (!response.ok) {
                    throw new Error(`Request to ${assessUrl} returned not-OK status: ${response.status} - ${response.statusText}`)
                }

                const responseBody = await response.json()
                results = responseBody.shift()

                results.push({
                    type: 'status',
                    name: 'dispatched',
                    value: 'server'
                })
            }
        }
        catch (e) {
            console.log(e)
            throw new Error('Error retrieving assessment')
        }

        results.push({
            type: 'status',
            name: 'mrx',
            value: 2
        })

        return results
    },

    async convert({ getters }, payload) {
        var results = []
        try {
            if (getters.useDeviceMRX) {
                await getters.loaded
                results = await window.mrx.logic.convert(payload)
            }
            else {
                const mrxConvertUrl = `${MRX_URL}/convert`
                const requestHeaders = {
                    'Content-Type': 'application/json',
                }
                const requestBody = JSON.stringify([payload])

                const response = await fetch(mrxConvertUrl, {
                    method: 'POST',
                    headers: requestHeaders,
                    body: requestBody
                })

                if (!response.ok) {
                    throw new Error(`Request to ${mrxConvertUrl} returned not-OK status: ${response.status} - ${response.statusText}`)
                }

                const responseBody = await response.json()
                results = responseBody.shift()
            }
        }
        catch (e) {
            console.log(e)
        }

        return results
    },

    async parseFHIRObservations({ getters }, payload) {
        var results = {}
        try {
            if (getters.useDeviceMRX) {
                await getters.loaded
                results = await window.mrx.logic.parseFHIRObservations(payload)
            }
            else {
                let response = await fetch(`${MRX_URL}/observations`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(payload)
                })
                results = await response.json()
            }
        }
        catch (e) { console.log(e) }
        return results
    },

    async formatOutputToFHIR({ getters }, payload) {
        var results = {}

        try {
            if (getters.useDeviceMRX) {
                await getters.loaded
                results = await window.mrx.logic.formatOutputToFHIR(payload)
            }
            else {
                let response = await fetch(`${MRX_URL}/fhir`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(payload)
                })
                results = await response.json()
            }
        }
        catch (e) { console.log(e) }
        return results
    },

    async load({ dispatch, rootGetters, getters }) {
        const loadedPromise = getters.loaded;
        let ok = true;
        try {
            if (!window.mrx && rootGetters['v4/settings/get']('offline')) {
                ok = false
                let req = await fetch('api/v3/mrx2.js')
                let data = await req.text()
                let query = await dispatch('v4/xsz/decodeAndDecompress', data, { root: true })
                if (query.ok) {
                    const runModule = new Function(query.data);
                    runModule();
                    loadedPromise.resolve(query.ok);
                }
                else {
                    loadedPromise.reject(query.ok);
                }
                ok = query.ok
            }
        }
        catch (e) {
            console.error('Error loading MRX:', e);
        }

        return ok
    },

    async assess({ dispatch, rootGetters, rootState, commit, getters }, payload = {}) {
        let results = []
        const arrayIsEmpty = (arr) => !Array.isArray(arr) || arr.length === 0

        if (getters['ready']) {
            try {
                const checkMrxErrors = !rootGetters['v4/mode/get']('disableMRXErrorChecking')
                const classifications = rootGetters['v4/track/classifications']
                let conditions = rootGetters['v4/track/conditions']

                if (payload.conditions) {
                    conditions = conditions.filter(condition => payload.conditions.includes(condition.name))
                }

                let responses = await dispatch('v4/panel/responses', { version: 4 }, { root: true })

                const responsesIsEmpty = arrayIsEmpty(responses)
                if (checkMrxErrors && responsesIsEmpty) {
                    throw new Error('MRX:ERR:1 - Responses did not convert')
                }

                let bundle = responses.concat(classifications, conditions)

                results = await Promise.race([
                    dispatch('_assess', bundle),
                    new Promise(function(resolve, reject) {  //Request timeout after 10sec
                        setTimeout(() => reject(new Error(REQUEST_TIMEOUT_ERROR_MESSAGE)), 10000)
                    })
                ]).catch((err) => {
                    results = []
                    if (checkMrxErrors) {
                        if (err.message === REQUEST_TIMEOUT_ERROR_MESSAGE) {
                            throw new Error('MRX:ERR:2 - Assessment call timed out', { cause: err })
                        } else {
                            throw new Error('Error during assessment call', { cause: err })
                        }
                    }
                })

                if (arrayIsEmpty(results)) {
                    results = []
                    if (checkMrxErrors) throw new Error('MRX:ERR:3 - Assessment did not return an array')
                }

                const statusTypeResults = results.filter(result => result.type === 'status')
                const performedObject = statusTypeResults.find(result => result.name === 'dispatched')
                const statusObject = statusTypeResults.find(result => result.name === 'clinical_logic')
                const assessmentTypeResults = results.filter(result => result.type === 'assessment')
                const conditionTypeResults = results.filter(result => result.type === 'condition')

                if (checkMrxErrors) {
                    if (!performedObject) {
                        throw new Error('MRX:ERR:4 - Assessment returned an empty bundle')
                    }

                    if (!statusObject) {
                        throw new Error('MRX:ERR:5 - No status object in bundle')
                    } else if (statusObject.value === 'invalid') {
                        throw new Error('MRX:ERR:6 - Bundle returned with clinical logic invalid')
                    }

                    if (assessmentTypeResults.length !== conditionTypeResults.length) {
                        throw new Error('MRX:ERR:7 - Not all conditions were assessed')
                    }
                }

                if (performedObject.value === 'server' && rootGetters['v4/settings/get']('offline')) {
                    dispatch('load')
                }

                const engagementFeatures = rootState.settings?.engagements_features || {};
                const currentTrack = rootGetters['v4/track/current'];
                results = applyCustomSupersedingRules(results, engagementFeatures, currentTrack);
                results = applyDisplayHiddenMetadata(results);

                if (rootGetters['v4/mode/get']('logScores')) {
                    console.log(
                        'hidden assessments:',
                        results
                            .filter(item => item.type === 'assessment' && item.display_hidden)
                            .map(a => ({ name: a.name, reason: a.display_hidden_reason }))
                    );
                }

                results.push({ type: 'status', name: 'validity', value: 'valid' })

                commit('setBundle', results)

                this._vm.$gtm.trackEvent({
                    event: 'triage',
                    category: 'triage',
                    action: 'severity',
                    label: getters.triage,
                    noninteraction: true,
                    age: age(rootGetters['v4/panel/getResponse']('age', 'days').value),
                    gender: rootGetters['v4/panel/checkResponse']('gender', 'male') ? 'male' : 'female'
                })

            }
            catch (e) {
                results.push({ type: 'status', name: 'validity', value: 'invalid' })
                results.push({ type: 'status', name: 'error', value: e.message })
                if (rootGetters['v4/settings/get']('offline')) {
                    results.push({ type: 'notification', name: 'mrx', value: 'must_refresh' })
                }
                commit('setBundle', results)
            }

        }
        else {
            results = getters['bundle']
        }

        if (rootGetters['v4/settings/get']('submitOnAssessment')) {
            dispatch('v4/stream/send', {}, { root: true })
            dispatch('v4/collect/send', {}, { root: true })
        }

        if (rootGetters['v4/mode/get']('logScores')) {
            let items = rootGetters['v4/mrx/assessments']()
            console.log(JSON.stringify(items, null, 2))
        }

        return results
    },

    async streamlinedOptions({ getters }, payload) {
        let results = {}
        try {
            if (getters.useDeviceMRX) {
                await getters.loaded
                results = await window.mrx.logic.streamlinedOptions(payload)
            }
            else {
                const mrxStreamlinedOptionsUrl = `${MRX_URL}/streamlinedOptions`
                const requestHeaders = {
                    'Content-Type': 'application/json',
                }
                const requestBody = JSON.stringify([payload])

                const response = await fetch(mrxStreamlinedOptionsUrl, {
                    method: 'POST',
                    headers: requestHeaders,
                    body: requestBody
                })

                if (!response.ok) {
                    throw new Error(`Request to ${mrxStreamlinedOptionsUrl} returned not-OK status: ${response.status} - ${response.statusText}`)
                }

                results = await response.json()
            }
        }
        catch (e) {
            console.log(e)
        }

        return results
    },

    async streamlinedEvaluation({ getters }, payload) {
        let results = {}
        try {
            if (getters.useDeviceMRX) {
                await getters.loaded
                results = await window.mrx.logic.streamlinedEvaluation(payload)
            }
            else {
                const mrxStreamlinedEvaluationUrl = `${MRX_URL}/streamlinedEvaluation`
                const requestHeaders = {
                    'Content-Type': 'application/json',
                }
                const requestBody = JSON.stringify([payload])

                const response = await fetch(mrxStreamlinedEvaluationUrl, {
                    method: 'POST',
                    headers: requestHeaders,
                    body: requestBody
                })

                if (!response.ok) {
                    throw new Error(`Request to ${mrxStreamlinedEvaluationUrl} returned not-OK status: ${response.status} - ${response.statusText}`)
                }

                results = await response.json()
            }
        }
        catch (e) {
            console.log(e)
        }

        return results
    }
}
